/**
 * Centralised, runtime-safe API base resolver.
 *
 * Why this exists
 * ----------------
 * `process.env.REACT_APP_BACKEND_URL` is inlined at BUILD time by CRA/Webpack.
 * If a production build is accidentally compiled with a preview/staging value,
 * every browser API call goes to the wrong origin and gets CORS-blocked. That
 * is the *exact* class of bug that broke funstay.in once already.
 *
 * The architecture already supports same-origin requests — the existing
 * `process.env.REACT_APP_BACKEND_URL || ''` pattern produces `fetch('/api/...')`
 * (relative URL) when the env var is empty. We extend that idea with a tiny
 * runtime safety net:
 *
 *   • If the env var is empty → same-origin (`""`). Existing behavior.
 *   • If the env var's hostname MATCHES `window.location.hostname` → use it.
 *     This is the normal preview/staging case — preserves current behaviour.
 *   • If the env var's hostname DOES NOT match the page's hostname → assume
 *     a misconfigured build and fall back to same-origin (`""`). The browser
 *     will hit `/api/...` on the host that's actually serving the page, which
 *     is what the user (and the routing/proxy layer) expects.
 *
 * This module is ADDITIVE — no schema/API/routing changes — and is a pure
 * string-returning helper. Callers can replace
 *   `process.env.REACT_APP_BACKEND_URL || ''`
 * with
 *   `getApiBase()`
 * one-for-one with no behavioural change in preview, staging, or a correctly
 * configured production deploy.
 *
 * The result is memoised — `window.location` is stable for the lifetime of
 * a page load and we don't want to re-parse the URL on every fetch.
 */

let cached = null;

const computeApiBase = () => {
  const env = process.env.REACT_APP_BACKEND_URL || '';

  // No env var → same-origin. Existing canonical fallback.
  if (!env) return '';

  // SSR / non-browser usage → trust the env var as-is.
  if (typeof window === 'undefined' || !window.location) return env;

  let envHost;
  try {
    envHost = new URL(env).hostname;
  } catch {
    // Malformed env value → safest is same-origin.
    return '';
  }

  const pageHost = window.location.hostname;

  // Hostnames match → preview/staging/correct-prod. Use env value verbatim.
  if (envHost === pageHost) return env;

  // Mismatch → the build was likely produced for a different deployment
  // target than the host that's actually serving it. Fall back to same-origin
  // so requests hit `/api/...` on the page's own host (which is where the
  // ingress/proxy is configured to route them).
  // eslint-disable-next-line no-console
  console.warn(
    `[apiBase] REACT_APP_BACKEND_URL host (${envHost}) does not match page host (${pageHost}). Falling back to same-origin.`
  );
  return '';
};

export const getApiBase = () => {
  if (cached === null) cached = computeApiBase();
  return cached;
};

export default getApiBase;
